import React, { useContext, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog
} from '@clientbase/clientbase-library'
import { Box, Tooltip, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import {
  amountTypeEnum,
  issueWhenEnum,
  natureOperationEnum
} from 'models/Invoice'
import moment from 'moment'
import InvoiceIcon from 'templates/Invoice/components/InvoiceIcon'

import CanLogical from 'components/CanLogical'

import { formatMoney } from 'utils'
import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

import { ModalChargeDetailContext } from '../../context'
import { cardTittle } from '../../ModalChargeDetail.utils'
import { S } from './'

const NFe: React.FC = () => {
  const { invoice, nfe, reload, updateReload } = useContext(
    ModalChargeDetailContext
  )

  const [confirmCancelScheduled, setConfirmCancelScheduled] = useState(false)
  const [confirmEmitNow, setConfirmEmitNow] = useState(false)

  // Cancelar o agendamento (destroy) exige nfses:cancel; "Emitir agora"
  // (emit_now) exige nfses:manage.
  const canCancelNfses = !!CanLogical({ permission: 'nfses:cancel' })
  const canManageNfses = !!CanLogical({ permission: 'nfses:manage' })

  // Item de serviço efetivo. O que saiu na nota vale mais que o configurado no
  // agendamento (que pode ter sido editado depois da emissão), mas a nota tem
  // que ser a DESTE agendamento e ainda válida:
  //
  // - `nfe` do contexto vem de `Billing#nfse`, que agrega os quatro caminhos de
  //   vínculo (nfse_scheduled, nfse_billings, installment, FK direta) e ordena
  //   por status — logo pode ser a nota antiga de uma reemissão, ou uma nota
  //   avulsa vinculada à mão. Não serve aqui.
  // - `nfsesScheduled.nfse` é a nota deste agendamento, e
  //   `issue_additional_nfse` a repontua na reemissão.
  // - Nota cancelada ou com falha volta a valer o agendamento, que é o que o
  //   "Emitir agora" vai usar.
  const scheduledNfse = invoice?.nfsesScheduled?.nfse
  const scheduledNfseIsValid =
    scheduledNfse?.status === 'confirmed' || scheduledNfse?.status === 'issued'
  const serviceItem =
    (scheduledNfseIsValid && scheduledNfse?.nfse_item?.nfse_issuer_service) ||
    invoice?.nfsesScheduled?.nfse_issuer_service

  // "Emitir agora" aparece em todo agendamento pendente. Fica desabilitado
  // quando o valor da nota é o valor pago e a fatura ainda não foi paga — sairia
  // nota com valor divergente (não há pagamento parcial no produto: ou paga, ou
  // não paga).
  const isBillingPaid = invoice?.status === 'paid'
  const disableEmitNow =
    invoice?.nfsesScheduled?.amount_type === 'amount_paid' && !isBillingPaid

  const emitNow = async () => {
    const id = toast.loading('Emitindo nota fiscal...')
    const response = await DataService({
      type: 'POST',
      url: `/v1/nfse_scheduleds/${invoice?.nfsesScheduled?.uuid}/emit_now`
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    setConfirmEmitNow(false)

    if (response.status === 200) {
      updateReload(!reload)
    }
  }

  const cancelScheduled = async () => {
    const id = toast.loading('Cancelando agendamento de emissão da NF...')
    const response = await DataService({
      type: 'DELETE',
      url: `/v1/nfse_scheduleds/${invoice?.nfsesScheduled?.uuid}`
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    setConfirmCancelScheduled(false)

    if (response.status === 200) {
      updateReload(!reload)
    }
  }

  if (!invoice?.nfsesScheduled) {
    return null
  }

  return (
    <Accordion
      sx={{
        border: '1px solid #E8EBE8',
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        borderRadius: '8px !important',
        backgroundColor: '#FAFBFA',
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
          backgroundColor: '#fff'
        }
      }}
    >
      <AccordionSummary aria-controls="payerDetails-content" id="payerDetails">
        {cardTittle(
          'Nota fiscal',
          <InvoiceIcon />,
          `${theme.palette.primary.main}`
        )}
      </AccordionSummary>
      <AccordionDetails>
        <>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin="10px 0px 10px 0px"
          >
            <Typography variant="smLight" color="#777777">
              Prestador de serviço:
            </Typography>
            <S.Separator />
            <Typography variant="smBold">
              {invoice?.nfsesScheduled?.nfse_issuer?.legal_name || ''}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin={'10px 0px 10px 0px'}
          >
            <Typography variant="smLight" color="#777777">
              ISS retido:
            </Typography>
            <S.Separator />
            <Typography variant="smBold">
              {invoice?.nfsesScheduled?.iss_retention === true
                ? 'Sim'
                : 'Não' || ''}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin={'10px 0px 10px 0px'}
          >
            <Typography variant="smLight" color="#777777">
              Emissão:
            </Typography>
            <S.Separator />
            <Typography variant="smBold">
              {issueWhenEnum[
                invoice?.nfsePolicy as keyof typeof issueWhenEnum
              ] || (invoice?.nfsePolicy as string)}
            </Typography>
          </Box>
          {((invoice?.nfsesScheduled as any)?.configuration_type === 1 ||
            (invoice?.nfsesScheduled as any)?.configuration_type ===
              'specific_day_of_month' ||
            (invoice?.nfsesScheduled as any)?.issue_mode ===
              'specific_day') && (
            <>
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                margin={'10px 0px 10px 0px'}
              >
                <Typography variant="smLight" color="#777777">
                  Dia do mês:
                </Typography>
                <S.Separator />
                <Typography variant="smBold">
                  {(invoice?.nfsesScheduled as any)?.day_of_month ||
                    (invoice?.nfsesScheduled as any)?.specific_day ||
                    ''}
                </Typography>
              </Box>
              <Box
                display="flex"
                gap={1.25}
                alignItems="end"
                margin={'10px 0px 10px 0px'}
              >
                <Typography variant="smLight" color="#777777">
                  Mês de emissão:
                </Typography>
                <S.Separator />
                <Typography variant="smBold">
                  {(() => {
                    const relVal = (invoice?.nfsesScheduled as any)
                      ?.due_date_relation as string
                    const mapStr: Record<string, string> = {
                      two_months_before: 'Dois meses antes do vencimento',
                      month_before: 'Mês anterior ao vencimento',
                      same_month: 'Mês do vencimento',
                      month_after: 'Mês seguinte ao vencimento',
                      two_months_after: 'Dois meses depois do vencimento'
                    }
                    if (relVal) return mapStr[relVal] || ''
                    const off = (invoice?.nfsesScheduled as any)
                      ?.specific_month_offset
                    const offMap: Record<string, string> = {
                      '-2': mapStr.two_months_before,
                      '-1': mapStr.month_before,
                      '0': mapStr.same_month,
                      '1': mapStr.month_after,
                      '2': mapStr.two_months_after
                    }
                    return offMap[String(off)] || ''
                  })()}
                </Typography>
              </Box>
            </>
          )}

          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin={'10px 0px 10px 0px'}
          >
            <Typography variant="smLight" color="#777777">
              Item de serviço:
            </Typography>
            <S.Separator />
            <Typography variant="smBold">
              {serviceItem?.code} - {''}
              {serviceItem?.description}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin={'10px 0px 10px 0px'}
          >
            <Typography variant="smLight" color="#777777">
              Valor da nota:
            </Typography>
            <S.Separator />
            <Typography variant="smBold">
              {amountTypeEnum[invoice?.nfsesScheduled?.amount_type as string] ||
                ''}{' '}
              {invoice?.nfsesScheduled?.amount
                ? `- ${formatMoney(Number(invoice?.nfsesScheduled?.amount))}`
                : ''}
            </Typography>
          </Box>
          {nfe?.created_at && (
            <Box
              display="flex"
              gap={1.25}
              alignItems="end"
              margin={'10px 0px 10px 0px'}
            >
              <Typography variant="smLight" color="#777777">
                Data/Hora emissão:
              </Typography>
              <S.Separator />
              <Typography variant="smBold">
                <Typography variant="smBold">
                  {moment(nfe?.created_at).format('DD/MM/YYYY HH:mm:ss')}
                </Typography>
              </Typography>
            </Box>
          )}
          {invoice?.nfsesScheduled?.nature_operation && (
            <Box
              display="flex"
              gap={1.25}
              alignItems="end"
              margin={'10px 0px 10px 0px'}
            >
              <Typography variant="smLight" color="#777777">
                Natureza de operação:
              </Typography>
              <S.Separator sx={{ marginTop: '4px' }} />
              <Typography variant="smBold">
                {
                  natureOperationEnum[
                    invoice?.nfsesScheduled?.nature_operation as string
                  ]
                }
              </Typography>
            </Box>
          )}

          {invoice?.nfsesScheduled?.copy_description ? (
            <Box
              display="flex"
              gap={1.25}
              alignItems="end"
              margin={'10px 0px 10px 0px'}
            >
              <Typography variant="smLight" color="#777777">
                Copiar descrição da cobrança
              </Typography>
              <S.Separator />
              <Typography variant="smBold">
                <Typography variant="smBold">
                  {invoice?.nfsesScheduled?.copy_description
                    ? 'Sim'
                    : 'Não' || ''}
                </Typography>
              </Typography>
            </Box>
          ) : (
            invoice.nfsesScheduled?.description && (
              <Box display="flex" flexDirection="column">
                <Typography variant="smLight" color="#777777" mb="6px">
                  Descrição:
                </Typography>
                <Typography variant="smBold">
                  {nfe?.nfse_item?.description}
                </Typography>
              </Box>
            )
          )}

          {(canCancelNfses || canManageNfses) &&
            invoice?.nfsesScheduled?.uuid && (
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                {canManageNfses && (
                  <Tooltip
                    title={
                      disableEmitNow
                        ? 'A fatura ainda não foi paga. Como o valor da nota é o valor pago, não é possível emitir agora.'
                        : ''
                    }
                  >
                    <span>
                      <Button
                        variantButton="outlined"
                        onClick={() => setConfirmEmitNow(true)}
                        disabled={disableEmitNow}
                      >
                        Emitir agora
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {canCancelNfses && (
                  <Button
                    variantButton="outlined"
                    onClick={() => setConfirmCancelScheduled(true)}
                  >
                    Cancelar agendamento de emissão da NF
                  </Button>
                )}
              </Box>
            )}

          <Dialog
            icon="info"
            title="Cancelar agendamento de emissão da NF?"
            description="A nota fiscal programada para esta cobrança não será emitida. Essa ação não pode ser desfeita."
            setIsOpenDialog={setConfirmCancelScheduled}
            isOpenDialog={confirmCancelScheduled}
            cancelButton
            cancelButtonLabel="Voltar"
            PaperProps={{ sx: { maxWidth: '480px' } }}
          >
            <Button
              onClick={cancelScheduled}
              fullWidth
              sx={{ whiteSpace: 'nowrap' }}
            >
              Cancelar agendamento
            </Button>
          </Dialog>

          <Dialog
            icon="info"
            title="Emitir a nota fiscal agora?"
            description="A nota fiscal será emitida imediatamente, sem esperar a condição do agendamento. Deseja prosseguir?"
            setIsOpenDialog={setConfirmEmitNow}
            isOpenDialog={confirmEmitNow}
            cancelButton
            cancelButtonLabel="Voltar"
            PaperProps={{ sx: { maxWidth: '480px' } }}
          >
            <Button onClick={emitNow} fullWidth sx={{ whiteSpace: 'nowrap' }}>
              Emitir agora
            </Button>
          </Dialog>
        </>
      </AccordionDetails>
    </Accordion>
  )
}

export default NFe
